/**
 * statusChips.tsx — the icon-led CLINICAL STATUS chip + the entry-path icon
 * chips (2026-08-26, CTO stage-walkthrough §3/§4).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: CRM pipeline.
 *
 * WHY A DERIVED CHIP instead of the old bare `eligibility` badge: staff read
 * the board mid-call, and "clinician review" as raw enum text answers a
 * different question ("what did the engine say?") than the one they are
 * actually asking ("where is the clinical decision?"). The states below
 * are the CTO's walkthrough vocabulary, derived CLIENT-SIDE from fields the
 * server already sends (R41 — nothing here is a new claim, only a rendering
 * of existing server facts).
 *
 * ── 2026-08-27: THE CHIP NOW READS THE DECISION, NOT THE ENGINE ─────────────
 *
 * The wave-1 derivation asked `eligibility` what the CLINICIAN had decided, and
 * `eligibility` cannot answer that question. Its only writers in clinic-service
 * are the submit-time engine verdict, the engine hard stop and the GDPR purge —
 * a clinician never touches it. Three of the five real-world states came out
 * wrong, and the CTO caught all three by opening one drawer on prod:
 *
 *   · `eligibility === 'not_eligible'` → "Clinician rejected" put a named
 *     profession behind a MACHINE's hard stop. No clinician had seen the
 *     patient. That is the most serious of the three: a clinical decision
 *     attributed to a human who never made one.
 *   · "Clinician approved" hung off `planSent`, which needs BOTH an
 *     AssessmentRecommendation AND an `assessment_handover_sent` audit row —
 *     artefacts of the plan-INVITE lane only. A patient who paid directly and
 *     whose order a clinician then approved could never reach the label.
 *   · "Pending clinician review" had NO stage guard, so a deal the clinician
 *     had already decided — sitting in Won or Lost — still claimed to be
 *     waiting for them. This is the chip the CTO photographed on a WON card.
 *
 * The fix is to derive each state from the fact that actually records it:
 *
 *   1. Clinician rejected  (red, cross)    — `closed_lost` AND the card's
 *      `closedReason` is the order-sync rejection lane's server-written
 *      sentinel (CLINICIAN_REJECTED_REASON). That lane is the ONE closure a
 *      clinician authors; every other closed_lost reason (an engine hard stop,
 *      a not-proceeding callback, a staff free-text close, a test-record mark)
 *      is somebody or something else, so it derives NOTHING — see below.
 *   2. Clinician approved  (green, check)  — the deal reached `closed_won` or
 *      `plan_sent`, or carries `planSent === true`. All three are approval
 *      facts: `closed_won` has NO manual path in (deal-stages.ts MANUAL_TARGETS
 *      lists none) and is reachable only through the orders-service order-sync
 *      `approved` event, which fires behind an Approval row stamped with the
 *      clinician's principal; `plan_sent` is entered only by the handover hook
 *      that sends the plan invite. The `planSent` FLAG stays because the hook
 *      deliberately leaves some lanes (call rounds, parks) where they are —
 *      but it NEVER outranks a stage with a live decision in it (see 4).
 *   3. Pending clinician review (amber, hourglass) — the engine referred the
 *      answers to a clinician (`eligibility === 'clinician_review'`) AND the
 *      deal is in a stage where that review is still OUTSTANDING
 *      (REVIEW_OUTSTANDING_STAGES). A decided deal never claims to be waiting.
 *
 * ── 2026-08-27 (CTO): PARKED IS NOT A CLINICAL STATUS ───────────────────────
 *
 * "Review parked" used to be a FOURTH member of this union, which made it
 * compete for the card's single clinical-status slot — and win, because its
 * branch ran first. The CTO's words: "the way you have parked right now pushes
 * main status down". Two things were wrong with it:
 *
 *   · It EVICTED the real status. A parked deal still has a clinical status —
 *     the review is outstanding, it is just blocked behind an admin — and the
 *     card stopped saying so, because one slot cannot hold two facts.
 *   · It was a full uppercase TEXT pill in the middle of the tag row, so it
 *     displaced everything after it and wrapped the row onto a second line.
 *
 * The category error is that a park is not a DECISION. Rejected / approved /
 * pending-review are mutually exclusive answers to "where is the clinical
 * decision?"; parked is an orthogonal HOLD that can sit on top of any of them.
 * So it is modelled that way now: `isReviewParked` is its own predicate and
 * `ReviewParkedChip` its own compact chip, rendered LAST in the tag row, and
 * `pending_admin_review` joins REVIEW_OUTSTANDING_STAGES so the status it used
 * to evict can appear beside it.
 *
 * ── 2026-08-27 (review of #296): AND AN UNDECIDED ONE NEVER CLAIMS IT IS ────
 *
 * The first cut of the fix above got the other direction wrong. A card can hold
 * an approval fact AND an outstanding one at once — a clinician sends a plan
 * invite (`planSent`), the patient then picks their own plan and checks out
 * NORMALLY, so clinic-service moves the deal to `awaiting_clinical_review` and
 * the clinician must still press approve on the paid order. The approval branch
 * short-circuited first, so that card wore a green "Clinician approved" while
 * sitting in the queue of PAID ORDERS a clinician triages — the CTO's defect
 * inverted, on the surface where a false green tick invites deprioritisation.
 * DECISION_OUTSTANDING_STAGES now runs BEFORE the approval branch: a stage that
 * means a decision is live outranks any earlier approval on the same card.
 *
 * NO CHIP IS THE SAFE ANSWER, and it is used deliberately. An engine hard stop
 * derives nothing at all: the card carries no actor, so a clinician's manual
 * close, an admin's, and the engine's are indistinguishable there — and naming
 * a clinician for a machine's decision is far worse than saying nothing. The
 * closure REASON still reaches a reader — the drawer's outcome block states it
 * in full and names the real author where the server recorded one, and the
 * board card carries it on the eligibility chip's tooltip and on the card's own
 * `title` (the red `CardClosedReason` line came off the card on 2026-09-06; see
 * PipelineBoard for why). Anything else with nothing to say (no eligibility
 * yet, a plain `eligible` verdict pre-plan) also derives no chip and callers
 * fall back to their existing rendering.
 *
 * ── 2026-09-06 (CTO): AND THE SLOT IS NEVER EMPTY ON THE FIRST THREE LANES ──
 *
 * Everything above is about a chip being WRONG. The remaining defect was a
 * chip being ABSENT: `clinicalStatusFor` answers "where is the clinical
 * DECISION?", and on a card nobody has decided anything about the honest
 * answer is `null` — which the board rendered as nothing at all. So a patient
 * who submitted a clean questionnaire and is waiting on a clinician's
 * signature looked IDENTICAL to a patient who has never opened the form. Two
 * blank cards, one of which is a job.
 *
 * `preDecisionChipFor` fills that slot on the board's first three lanes only,
 * with a second question — "how far has this patient got, and what did we
 * find?" — and never at the expense of a decision the card already carries.
 * Its states are two questionnaire GHOSTS (not started / in progress) and a
 * two-half REVIEW chip whose amber left half is constant across every verdict,
 * so a lane of outstanding work scans as one unbroken stripe.
 *
 * ⚠️ ITS ONE TRAP, restated here because it is the kind that ships quietly:
 * `hasClinicalFlags` is OPTIONAL, and an absent field means "we were not
 * told", never "there are no flags". `Review │ clear` on a card whose flags we
 * have never received is a false clinical reassurance to the person deciding
 * which patient to open first. Absent falls back to the plain amber chip. See
 * `reviewVerdictFor`.
 *
 * WCAG 2.1 AA: every CLINICAL STATUS chip is icon + VISIBLE TEXT (never colour
 * alone), with `title` + `aria-label` carrying the full phrase — the card wears
 * the short text, the drawer the full phrase, and both read identically to a
 * screen reader. The ICON-ONLY chips in this module — the four entry-path chips
 * and `ReviewParkedChip` — satisfy 1.4.1 by SHAPE rather than by text, and
 * carry their full wording on `title` + `aria-label` with `role="img"`. See
 * `ReviewParkedChip` for why that trade is the right one for a secondary
 * qualifier and is NOT available to the clinical-status chip, whose three
 * states are told apart by hue as much as by glyph.
 */
import type { CSSProperties, ReactElement } from 'react';
import type { DealCard, DealEntryPath, DealStage } from '../api/types';
import { CHIP_TONES } from '../ui/tones';
import { ENTRY_LABEL, isAwaitingClinicalDecision } from './stages';

/**
 * The three MUTUALLY EXCLUSIVE answers to "where is the clinical decision?",
 * in the order they are checked. A park is deliberately NOT one of them — see
 * the module doc and `isReviewParked`.
 */
export type ClinicalStatus = 'rejected' | 'approved' | 'pending_review';

/**
 * The SERVER-WRITTEN SENTINEL the order-sync rejection lane stamps as the
 * deal's closing reason (clinic-service `deals.service.ts` — the `rejected`
 * event writes `closedReason: 'clinically rejected'` onto the Deal row AND onto
 * the closing audit transition, and the card's `closedReason` is derived from
 * exactly those two sources in that order).
 *
 * WHY MATCH A STRING rather than read a closure-kind flag: the wire carries no
 * discriminator, by design — the drawer aggregate tells the two closures apart
 * by their ACTOR (`outcome.closedByRole`), but the BOARD CARD has no actor
 * field, and the derivation has to be right at source for both surfaces. This
 * sentinel is the only positive, machine-written evidence on the card that a
 * CLINICIAN authored the closure. It is not staff free text: no human types it.
 *
 * FAILS SAFE. If clinic-service ever reworded the lane, the chip would simply
 * stop rendering and the closure reason line would carry the fact on its own —
 * a silence, never a false attribution. That is the correct failure direction
 * for a label that names a profession.
 */
export const CLINICIAN_REJECTED_REASON = 'clinically rejected';

/**
 * The stages in which a clinician's review is genuinely still OUTSTANDING —
 * the gate the "Pending clinician review" chip was missing entirely.
 *
 * An ALLOW-LIST on purpose. The three stages deliberately absent are the ones
 * where the clinical decision has already been MADE: `plan_sent` and
 * `closed_won` (approved) and `closed_lost` (ended).
 * A stage added to the vocabulary later joins this list only by a DELIBERATE
 * edit; until then it derives no chip, which is the safe direction — a missing
 * chip loses information, a wrong one asserts a clinical falsehood. The spec
 * pins that every stage in DEAL_STAGES is accounted for either here or as one
 * of those three, so a new stage cannot be added without someone choosing.
 *
 * `unreachable` IS here (renamed from `marketing_funnel`, 2026-09-05): the rot
 * cron parks a stale `clinical_review` deal there without anyone having looked
 * at it, the callbacks machine parks one there after six unanswered calls, and
 * since 2026-09-22 an admin may put one there by hand. In all three the review
 * really is still owed — hiding the chip would bury a
 * clinical backlog in the lane nobody is looking at. Under the OLD name this
 * was the argument that the chip must not hide behind a "nurture" label; the
 * lane is honestly named now and the reasoning is unchanged.
 *
 * `pending_admin_review` IS here since 2026-08-27 (CTO), and it is the one
 * entry that CHANGED SIDE. It used to be excluded because the deal "wears
 * Review parked instead" — but a park is a HOLD, not a decision: nobody has
 * decided anything, the clinician has handed the deal to an admin and still
 * owes the review afterwards. Excluding it meant a parked card said only that
 * it was parked and went silent on the outstanding clinical review, which is
 * the more important of the two facts. The park now renders as its own
 * trailing chip (`ReviewParkedChip`) so both can be true at once.
 */
export const REVIEW_OUTSTANDING_STAGES: readonly DealStage[] = [
  'mql',
  'callback_requested',
  /**
   * `warm_lead` IS here (2026-09-07, the authorisation gate), and the choice is
   * the opposite of what the stage's name suggests.
   *
   * The gate stopped an unauthorised patient from being ROUTED to a clinician;
   * it did not decide anything about them clinically. Their questionnaire is
   * finished and nobody has ruled on it — the review is genuinely still owed,
   * which is exactly the argument that already puts `mql` (a card with no
   * questionnaire at all) on this list. Leaving it off would make the chip go
   * silent on the largest cohort on the board precisely because we chose not to
   * queue them, and that is the direction that asserts a falsehood: "nothing
   * outstanding" about someone whose form is sitting there unread.
   */
  'warm_lead',
  // ONE ENTRY WHERE THERE WERE TWO (2026-09-05). Both `eligibility_in_progress`
  // and `awaiting_clinical_review` were on this list, so the merged lane
  // inherits the membership from both halves with nothing to arbitrate.
  'clinical_review',
  'awaiting_clinician_followup',
  'awaiting_admin_followup',
  'pending_admin_review',
  'unreachable',
];

/**
 * Is a NEW clinical decision OUTSTANDING ON THE CARD ITSELF — so any approval
 * fact the card also carries is STALE and must not speak (2026-08-27, review
 * of #296)?
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THIS WAS A STAGE LIST UNTIL 2026-09-05, AND IT COULD NOT STAY ONE.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It used to read `DECISION_OUTSTANDING_STAGES = ['awaiting_clinical_review']`,
 * and its own note explained that this was "the one stage that means a PAID
 * order is in the clinician's queue right now". The restructure merged that
 * stage into `clinical_review` together with `eligibility_in_progress`, so the
 * stage name no longer carries the distinction — and BOTH naive translations
 * are wrong in a way somebody would act on:
 *
 *  - map it to `['clinical_review']` and every UNPAID card in the merged lane
 *    starts suppressing its approval chip. That is the original defect
 *    inverted: a clinician who approved and sent a plan invite would watch the
 *    card go silent about it, which is exactly the "nothing happened" reading
 *    that made the CTO order the `plan_sent` lane in the first place.
 *  - drop it and the PAID cards fall through to the approval branch and wear a
 *    green "Clinician approved" tick while sitting in the queue of paid orders
 *    a clinician is meant to triage — the CTO's own photographed defect, on the
 *    more dangerous surface, where a green tick invites deprioritisation.
 *
 * So it follows the server, which keyed the same rule on the fact that actually
 * differed rather than on a stage name (`isAwaitingClinicalDecision`). Money
 * behind the card is what makes the decision outstanding; the lane it sits in
 * never was.
 *
 * ⚠️ THAT FACT IS `holdAuthorisedAt` SINCE 2026-09-07, NOT `orderCaseRef`. The
 * authorisation gate split checkout into two events, and `orderCaseRef` is
 * stamped by the one that means the patient did NOT authorise a card — so the
 * old key made this chip fire on every warm lead an admin pulled into review,
 * announcing an outstanding decision on money that was never committed.
 *
 * THE CARD THIS EXISTS FOR, unchanged. A clinician recommends a plan and the
 * handover sends the invite (`planSent` true). The patient then picks their own
 * plan and AUTHORISES a card — orders-service reports that as `authorised`, not
 * `approved` — so clinic-service stamps the authorisation and puts the deal in
 * `clinical_review`, and the clinician must STILL press approve on the paid
 * order.
 *
 * IT STAYS DELIBERATELY NARROW. The call-round lanes (callback_requested /
 * awaiting_*_followup) are NOT covered, whatever their authorisation says: the
 * handover hook deliberately leaves those deals where they are, so `planSent`
 * is the only record the approval has there and silencing it would re-create
 * the original defect. The predicate is pinned to the one stage for that
 * reason.
 *
 * THE SIBLING RULE, already in the codebase: `showPlanSentChip`
 * (PipelineBoard.tsx) hides the "Plan sent" chip on exactly the
 * order-progressed cards for the same reason — "the stage says MORE" — and it
 * reads this same predicate.
 */
export function isDecisionOutstanding(card: Pick<DealCard, 'stage' | 'holdAuthorisedAt'>): boolean {
  return isAwaitingClinicalDecision(card);
}

/**
 * Derive the deal's clinical status from server facts on the card (the drawer
 * passes the detail's `deal`, which carries the same shape). Null = none of
 * the four states applies — the caller renders its existing fallback.
 *
 * ORDER MATTERS, and the first two branches are both PRECEDENCE rules over the
 * approval branch — a card can carry an approval fact and an unfinished one at
 * the same time, and the unfinished one is the newer question:
 *
 *  1. `closed_lost` leads for the same reason the old code put "rejected"
 *     first: a terminal closure is the last clinical word on the deal, so a
 *     stale `planSent` from an earlier plan invite must never repaint it
 *     "approved". The difference is that it now returns a chip ONLY for the one
 *     closure a clinician authored, and NULL for every other way a deal is lost.
 *  2. DECISION_OUTSTANDING_STAGES (2026-08-27, review of #296) then outranks
 *     the approval branch, for the same reason one step earlier in the journey:
 *     a PAID ORDER awaiting the clinician is a live question, and the plan
 *     invite that preceded it answered a different one.
 */
export function clinicalStatusFor(card: DealCard): ClinicalStatus | null {
  if (card.stage === 'closed_lost') {
    // The ONE positively clinician-authored closure. Everything else that
    // lands here — an engine hard stop, a not-proceeding callback, a staff
    // free-text close, a test-record mark, or an older clinic-service that
    // sends no `closedReason` at all — derives NOTHING rather than a guess.
    // Trimmed like `cardClosedReason` so a padded string is still the sentinel.
    return card.closedReason?.trim() === CLINICIAN_REJECTED_REASON ? 'rejected' : null;
  }
  if (card.stage === 'closed_duplicate') {
    /**
     * A MERGED DUPLICATE DERIVES NOTHING (2026-09-06) — the same rule as the
     * `closed_lost` branch above, and for a sharper reason. This card carries
     * whatever clinical facts its own journey collected, `planSent` included,
     * and without this guard a merged card would fall through to the approval
     * branch and wear a green "Clinician approved" tick. That tick would be
     * pointing at a plan the patient is being served on the OTHER card, on a
     * record staff have deliberately taken out of the working board.
     *
     * There is no duplicate-flavoured chip to return in its place: "merged" is
     * not a clinical status, it is a bookkeeping one, and it is already stated
     * twice over by the stage badge and the `[Duplicate]` marker. Null is the
     * caller's cue to fall back, which on this card means the closure line.
     */
    return null;
  }
  if (isDecisionOutstanding(card)) {
    // A decision is OUTSTANDING here, so no earlier approval fact may speak —
    // this returns before the approval branch below can read `planSent`. The
    // engine's referral is still what the amber chip claims (this fix narrows
    // the green one, it does not widen the amber one), so a card the engine
    // cleared falls to NULL: no claim either way, which is the safe direction
    // and the one thing that must never happen here is a green tick on a paid
    // order nobody has reviewed.
    return card.eligibility === 'clinician_review' ? 'pending_review' : null;
  }
  // Approval, from any of its three server facts. `closed_won` and `plan_sent`
  // are BOTH clinician-only entries (no manual path into either), so the stage
  // alone is the approval — and `plan_sent` is NOT redundant beside the flag
  // (review of #296, re-derived against clinic-service): the stage is written
  // by `syncDealOnPlanSent` on the handover send alone, while the card's
  // `planSent` needs that AND an `AssessmentRecommendation` row, and
  // `handover()` does not require one. A clinician who sends the plan invite
  // without a saved recommendation lands in `plan_sent` with the flag FALSE,
  // and the stage is then the only approval evidence the card carries.
  // The flag covers the lanes the handover hook deliberately leaves in place
  // (call rounds, parks). `=== true` — an absent planSent (older server)
  // claims nothing.
  if (card.stage === 'closed_won' || card.stage === 'plan_sent' || card.planSent === true) return 'approved';
  // NOTE (2026-08-27, CTO): a `pending_admin_review` branch used to sit here,
  // returning 'parked' and short-circuiting the review check below. It is gone
  // — the park is now `isReviewParked` + its own chip, and the stage is inside
  // REVIEW_OUTSTANDING_STAGES, so a parked deal reaches the line below and
  // reports the review it still owes.
  // The stage guard is the fix for the chip the CTO photographed: the engine's
  // referral is only half the fact — the review must still be OUTSTANDING.
  if (card.eligibility === 'clinician_review' && REVIEW_OUTSTANDING_STAGES.includes(card.stage)) return 'pending_review';
  return null;
}

/* ══════════════════════════════════════════════════════════════════════════
 * THE PRE-DECISION SLOT (2026-09-06, CTO — sections 3/4/5 of the card review)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ THE BUG THIS EXISTS FOR IS NOT THE CARD'S HEIGHT. `clinicalStatusFor`
 * above answers "where is the clinical DECISION?", and on a card where nobody
 * has decided anything yet the honest answer is `null` — which the card then
 * rendered as NOTHING. So a patient who submitted a clean questionnaire and is
 * waiting for a clinician's signature looked EXACTLY like a patient who has
 * never opened the form: two blank cards, one of which is a job and one of
 * which is not. Reserving the row fixes the ragged heights; filling it is what
 * fixes the ambiguity, and the ambiguity was the expensive half.
 *
 * SO THIS SLOT ANSWERS A DIFFERENT QUESTION — "how far has this patient got,
 * and what did we find?" — and it only applies where that question has no
 * answer yet (PRE_DECISION_STAGES). It never overrides an approval or a
 * rejection: a decided card keeps the chip `clinicalStatusFor` derives, on
 * every lane, exactly as before.
 *
 * TWO HALVES, AND THE LEFT ONE NEVER MOVES. The three review states share one
 * amber "Review" half — same glyph, same hue, same width — and differ only in
 * the verdict half beside it. Scanning forty cards, the outstanding work is
 * then a single unbroken amber stripe down the lane and the eye only has to
 * read the right-hand end of it. Break that and the chip stops being scannable
 * and becomes three chips that happen to share a word.
 *
 * "NOT ELIGIBLE" IS GREY AND THAT IS DELIBERATE. A hard stop is a routine
 * outcome — the engine does its job several times a day — while a clinical
 * flag is a thing a human has to read. If both shout in red, the red stops
 * meaning "read me" within a week and the flag is the one that suffers.
 */

/**
 * The lanes where NO clinical decision has been made yet — the board's first
 * four columns, and the only ones this slot speaks on.
 *
 * WHY THESE FOUR AND NOT A "not decided" PREDICATE. Every later lane has a
 * decision behind it and already says so: `plan_sent` and `closed_won` are
 * approvals, `closed_lost` and `closed_duplicate` are closures, the two
 * follow-up lanes and `pending_admin_review` are places a human deliberately
 * PUT the card, and `unreachable` is somebody giving up on the phone — the
 * call machine, or (2026-09-22) an admin saying so by hand. On all eight
 * the existing chips are the right ones and re-litigating the questionnaire
 * there would be noise. These four are the only lanes where "has this person
 * even filled the form in?" is still a live question.
 *
 * ⚠️ `warm_lead` JOINED ON 2026-09-07 (the authorisation gate), and it is the
 * lane this slot matters MOST on. It was three columns until then. The CTO's
 * whole ask for the lead lane is that staff can "reach out to them while seeing
 * WHERE THEY STOPPED" — and this chip is the only thing on the board card that
 * answers that. Leaving the lane out would have shipped a chase list with the
 * one fact a chaser needs stripped out of it.
 *
 * AN ALLOW-LIST, like REVIEW_OUTSTANDING_STAGES above and for the same reason:
 * a stage added to the vocabulary later joins by a DELIBERATE edit, and until
 * someone makes it the card falls back to exactly what it renders today. The
 * spec pins this list against the board's own leading columns, so a reordering
 * of DEAL_STAGES cannot silently move the boundary.
 */
export const PRE_DECISION_STAGES: readonly DealStage[] = [
  'mql',
  'callback_requested',
  'warm_lead',
  'clinical_review',
];

/** The right-hand half of the Review chip — what we found, if anything. */
export type ReviewVerdict = 'clear' | 'flagged' | 'not_eligible';

/** What the pre-decision slot renders. `review` with a null verdict is the
 *  plain amber chip: a review is owed and we are not claiming to know more. */
export type PreDecisionChip =
  | { kind: 'not_started' }
  | { kind: 'in_progress' }
  | { kind: 'review'; verdict: ReviewVerdict | null };

/**
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ THE ONE THING THIS FILE MUST NOT GET WRONG: `undefined` IS NOT `false`.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `hasClinicalFlags` is OPTIONAL on the wire. A clinic-service older than the
 * field simply omits it, and an omission means "nobody told us", not "there is
 * nothing to tell". Rendering `Review │ clear` off an absent key would put a
 * green all-clear on a card whose flags we have never been sent — a FALSE
 * CLINICAL REASSURANCE, on the surface a clinician uses to decide which
 * patient to open first. That is the single outcome this slot must never
 * produce, so `clear` is gated on an EXPLICIT `false` and an absent field
 * falls back to the plain amber `Review` with no verdict half at all.
 *
 * `eligible`/`clinician_review` are BOTH clear-eligible: the second is not a
 * clinical worry in itself — clinic-service refers on non-clinical routing
 * markers too, which is exactly why `clinicalFlagsOf` strips them out of the
 * flag vector — so "the engine referred you AND there are no flags" is a real,
 * and reassuring, state. A NULL eligibility is not: we do not know the
 * verdict, so we do not summarise it.
 *
 * WHEN A HARD STOP AND A FLAG ARE BOTH TRUE, the verdict half says NOT
 * ELIGIBLE and the flag keeps its own chip (`ClinicalFlagsChip`, rendered
 * beside this one). The rule is "whichever fact has no other carrier wins the
 * shared slot": the flags have a chip of their own, the engine's verdict does
 * not, so putting the verdict here loses nothing and putting the flag here
 * would lose the hard stop entirely. The board still shows both facts; only
 * which one is spelt out in words changes.
 */
function reviewVerdictFor(card: DealCard): ReviewVerdict | null {
  if (card.eligibility === 'not_eligible') return 'not_eligible';
  if (card.hasClinicalFlags === true) return 'flagged';
  // ⚠️ `=== false`, never `!card.hasClinicalFlags`. See the block above: the
  // falsy-check spelling is the false-reassurance bug, written the short way.
  // ⚠️ `eligible` ONLY. `clinician_review` WITH AN EMPTY FLAG VECTOR IS NOT A
  // CLEAR CARD, and the widening that included it was overruled on review.
  // Two independent reasons, either sufficient:
  //  1. clinic-service already ruled on this exact combination and ruled the
  //     other way. `isWeightPhotoRequired` treats
  //     `flags.length === 0 && eligibility === 'clinician_review'` as
  //     UNEXPLAINED CLINICIAN INVOLVEMENT and classifies it CLINICAL — "the
  //     failure we accept is asking for a photo we did not strictly need,
  //     never skipping one on a patient a prescriber is about to sign for".
  //     A green "no clinical flags" on the card contradicts the server's own
  //     reading of the same row, on the surface staff triage from.
  //  2. `hasClinicalFlags` is `clinicalFlagsOf(flags).length > 0`, and
  //     `clinicalFlagsOf` DEGRADES a malformed or hand-edited Json vector to
  //     `[]` rather than throwing. So a corrupt read arrives here as `false`,
  //     and pairing it with a referral would render a degraded read as an
  //     affirmative all-clear. Silence is the safe rendering of "we do not
  //     know"; a green verdict is not.
  // A referred card with no flags therefore falls through to plain `Review`.
  if (card.hasClinicalFlags === false && card.eligibility === 'eligible') return 'clear';
  return null;
}

/**
 * What fills the card's status slot on the first three lanes — or `null`
 * everywhere else, which is the caller's cue to keep rendering exactly what it
 * renders today.
 *
 * ORDER MATTERS, and the review branch LEADS on purpose. `clinicalStatusFor`
 * saying `pending_review` means a clinician's review is genuinely outstanding
 * on this card; if a stale or contradictory `assessmentState` could reach the
 * draft branch first, the amber half would be swallowed by a ghost chip and
 * the board would file a live clinical job under "in progress". The
 * questionnaire branches only ever run on a card no review is owed on.
 */
export function preDecisionChipFor(card: DealCard): PreDecisionChip | null {
  if (!PRE_DECISION_STAGES.includes(card.stage)) return null;
  if (clinicalStatusFor(card) === 'pending_review') return { kind: 'review', verdict: reviewVerdictFor(card) };
  // BOTH fields null, not just the id: a card that reports a draft state has
  // started, whatever its id says, and calling that "not started" would file a
  // patient who is mid-form under the ones who have never seen it.
  if (card.assessmentId == null && card.assessmentState == null) return { kind: 'not_started' };
  if (card.assessmentState === 'draft') return { kind: 'in_progress' };
  // Submitted, and no decision on the card. THIS is the state that used to
  // render nothing at all — the clean, eligible patient waiting for a
  // signature, indistinguishable from a patient who never started.
  return { kind: 'review', verdict: reviewVerdictFor(card) };
}

/** Full phrase (tooltip + aria-label + drawer text) and the card's short text. */
const STATUS_COPY: Record<ClinicalStatus, { full: string; short: string }> = {
  pending_review: { full: 'Pending clinician review', short: 'Review' },
  approved: { full: 'Clinician approved', short: 'Approved' },
  rejected: { full: 'Clinician rejected', short: 'Rejected' },
};

/** The park's full wording — unchanged, so nothing a reader had learned to
 *  recognise (or a screen reader had been announcing) is renamed by this
 *  change; only where and how it renders moved. */
const PARKED_LABEL = 'Review parked';

/** Chip colours reuse the Badge tone tokens so light/dark just work. */
const STATUS_TOKENS: Record<ClinicalStatus, { bg: string; text: string }> = {
  pending_review: { bg: 'var(--gps-warn-bg)', text: 'var(--gps-warn-text)' },
  approved: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  rejected: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' },
};

/** The park keeps the grey it already wore — a hold is not a verdict, and the
 *  three verdict hues (amber/green/red) must stay unambiguous beside it. Read
 *  from CHIP_TONES since 2026-09-06: this pair was typed out twice in this file
 *  once the staff-provenance chip also needed grey. */
const PARKED_TOKENS = CHIP_TONES.muted;

/**
 * ONE CHIP HEIGHT, AND THE CARD'S STATUS ROW IS RESERVED AT EXACTLY IT
 * (2026-09-06, CTO — the constant-height card).
 *
 * A board card used to be as tall as whatever happened to be on it, and the
 * status row was half the reason: a card with no chip had no row at all, so it
 * sat a chip's height shorter than the card above it and the lane read as a
 * ragged stack. The fix is a floor, and a floor only works if every chip that
 * can sit on it agrees what "one chip" is.
 *
 * `minHeight` + `boxSizing: 'border-box'` TOGETHER, and the pair is the whole
 * point. The ghost chips below draw a 1px border and the filled ones do not,
 * so on the default `content-box` the two would differ by exactly 2px — a
 * card-height difference decided by whether a patient has opened the form,
 * which is the ragged stack again, one chip smaller. Border-box makes the
 * border eat into the height instead of adding to it.
 *
 * ⚠️ 22px IS ONLY TRUE IF THE LINE BOX IS PINNED, and it was not. The comment
 * here used to justify 22px as "a ~13.2px line box in 8px of vertical padding",
 * which assumes `line-height: normal`. `tokens.css` sets `body { line-height:
 * 1.5 }` and every chip inherits it, so a filled chip measured **24.5px** in
 * Chrome and this floor bound on nothing but the icon-only chips — which were
 * therefore 2.5px SHORTER than every chip with text, and the bordered ghosts
 * 2px TALLER than the filled ones. Exactly the ragged stack the floor exists to
 * remove, and jsdom cannot see it: the test asserts the constant equals itself.
 *
 * `chipStyle` now sets `lineHeight: '13.2px'` explicitly. 11px * 1.2 = 13.2px
 * in 8px of padding is 21.2px, so the 22px floor BINDS. Do not remove the
 * explicit line height and leave the number: the two are one fix.
 *
 * ⚠️ AND `boxSizing: 'border-box'` IS NOT WHAT EQUALISES THE BORDERED GHOSTS —
 * that claim was made three times in this file and the docs and the browser
 * disproves it. Border-box means the floor is MEASURED across the border; it
 * does not stop a border pushing the element past that floor. A ghost is
 * 13.2 line + 8 padding + 2 border = 23.2px, already above 22px, so the floor
 * never binds and the ghost stands 1.2px proud. What equalises them is the
 * ghost paying its border out of its own padding (3px/9px — see
 * `QuestionnaireGhostChip`). Measured in Chrome after the fix: filled 22.0,
 * ghost 22.0, icon-only 22.0, every one of them.
 */
export const CHIP_HEIGHT = '22px';

/** Shared chip chrome. ⚠️ It does NOT actually mirror `ui/kit`'s Badge — this
 *  comment claimed it did until 2026-09-07, when the heights were measured in a
 *  browser: these chips are 22.0px and a real Badge is 24.5px, because Badge
 *  inherits `body { line-height: 1.5 }` and these pin their line box. They sit
 *  BESIDE real Badges and are deliberately a little tighter; if you need them
 *  to match exactly, change Badge, not this. Kept close to Badge so the chips
 *  read as one family with real
 * Badges. EXPORTED so the stage-clarity chips (stageClarity.tsx) sit in the
 * same card row at the same size without a second copy of this geometry
 * drifting away from it. */
export const chipStyle = (t: { bg: string; text: string }): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: t.bg,
  color: t.text,
  fontSize: '11px',
  fontWeight: 700,
  // ⚠️ EXPLICIT, not inherited. `body { line-height: 1.5 }` made the real line
  // box 16.5px and every height below a lie — see CHIP_HEIGHT.
  lineHeight: '13.2px',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  padding: '4px 10px',
  borderRadius: 'var(--gps-r-pill)',
  // See CHIP_HEIGHT: the two lines below are what stop a bordered chip and a
  // filled one disagreeing about how tall a card is.
  boxSizing: 'border-box',
  minHeight: CHIP_HEIGHT,
});

/* Inline SVGs (no new deps — the kit's shared-icon rule): decorative
 * (`aria-hidden`), `currentColor` inherits the chip's text colour. */

/**
 * ONE STROKE WEIGHT FOR EVERY OUTLINE GLYPH IN THIS MODULE (2026-09-06).
 *
 * The four outline glyphs had drifted into two sets: the hourglass was drawn at
 * 2 and the tick, cross and pause at 3. They sit in the SAME chip row, inches
 * apart, so the difference did not read as "a thinner icon" — it read as two
 * icon families from two different design systems, which is exactly the kind of
 * incoherence a scan surface punishes.
 *
 * 3 IS THE NUMBER, not 2, because these glyphs now share a row with SOLID
 * silhouettes (the provenance chips below). Against filled mass a 2-weight
 * outline renders at 1 device pixel on a 12px chip and reads as a hairline
 * afterthought; 3 renders at 1.5px and holds its own. The hourglass's waist is
 * the one place the heavier stroke could clog — it is a point where two curves
 * meet — and at 1.5px it stays a waist rather than a blob, which is what the
 * shape is meant to say anyway.
 *
 * A NEW GLYPH READS THIS CONSTANT. That is the whole point of it existing:
 * the drift above happened because four literals had no reason to agree.
 */
const GLYPH_STROKE = 3;

const svgProps = (size: number) =>
  ({
    'aria-hidden': true,
    role: 'img',
    viewBox: '0 0 24 24',
    width: size,
    height: size,
    xmlns: 'http://www.w3.org/2000/svg',
    style: { flexShrink: 0, display: 'inline-block', verticalAlign: '-0.125em' } as CSSProperties,
  }) as const;

function HourglassGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth={GLYPH_STROKE}
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 3h12M6 21h12M7 3c0 5 4 5 5 8-1 3-5 3-5 8M17 3c0 5-4 5-5 8 1 3 5 3 5 8"
      />
    </svg>
  );
}

function TickGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path fill="none" stroke="currentColor" strokeWidth={GLYPH_STROKE} strokeLinecap="round" strokeLinejoin="round" d="M4 12.5l5.5 5.5L20 6.5" />
    </svg>
  );
}

function CrossGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path fill="none" stroke="currentColor" strokeWidth={GLYPH_STROKE} strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function PauseGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path fill="none" stroke="currentColor" strokeWidth={GLYPH_STROKE} strokeLinecap="round" d="M9 5v14M15 5v14" />
    </svg>
  );
}

const STATUS_GLYPH: Record<ClinicalStatus, (props: { size?: number }) => ReactElement> = {
  pending_review: HourglassGlyph,
  approved: TickGlyph,
  rejected: CrossGlyph,
};

/**
 * The VERDICT half's wording and tone (2026-09-06). The `full` phrase is
 * appended to the status phrase for `title` + `aria-label`, so a screen reader
 * hears one sentence rather than two fragments.
 *
 * TONES, and why each is the one it is:
 *  · `clear` reuses the APPROVED chip's green pair — it is the board's
 *    established "nothing wrong here", already measured, and reading it as
 *    "approved" is prevented by the amber half sitting immediately to its left
 *    saying REVIEW.
 *  · `flagged` is the danger pair (`--gps-danger-text` on `--gps-danger-bg`,
 *    4.97:1 — see the note beside `not_eligible` in ui/tones.ts). RED IS SPENT
 *    HERE AND ESSENTIALLY NOWHERE ELSE ON AN OPEN CARD, which is the entire
 *    reason it still works as an alarm.
 *  · `not_eligible` takes the MUTED grey, deliberately NOT the danger red that
 *    `toneFor('not_eligible')` gives the same word elsewhere. That map colours
 *    a CLOSED deal's verdict, where red is right because the sale stopped; on
 *    an open card in a working lane the hard stop is a routine outcome a human
 *    is about to look at, and if it shouts as loudly as a clinical flag then
 *    within a week the flag stops being read. Grey is the board's existing
 *    "true, but not the thing to act on".
 */
const VERDICT_COPY: Record<ReviewVerdict, { full: string; short: string }> = {
  clear: { full: 'no clinical flags', short: 'clear' },
  flagged: { full: 'clinical flags raised', short: 'flagged' },
  not_eligible: { full: 'eligibility check: not eligible', short: 'not eligible' },
};

const VERDICT_TOKENS: Record<ReviewVerdict, { bg: string; text: string }> = {
  clear: { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' },
  flagged: { bg: 'var(--gps-danger-bg)', text: 'var(--gps-danger-text)' },
  not_eligible: CHIP_TONES.muted,
};

/** One half of a split chip. The outer pill owns the radius and the clipping,
 *  so each half is a plain rectangle and the two meet on a hard edge — which
 *  is what makes the join read as one chip in two parts rather than as two
 *  chips that have drifted together. */
const chipHalfStyle = (t: { bg: string; text: string }): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  background: t.bg,
  color: t.text,
  // ⚠️ 4px, not the pill's 10px, and the 6px it saves per edge is not cosmetic.
  // Two halves means FOUR edges, so the pill's own padding costs 24px over a
  // single chip before a letter is drawn. Measured on the real board: at 6px
  // the widest chip (`not eligible`) was 117px and `117 + 6 gap + 134.2
  // Action needed = 257.2` against a 250px row — it wrapped, and the card was
  // 32px taller than on dev. At 4px the chip is 109px, the row is one line and
  // the card is back to 105px. The two halves are told apart by their FILLS,
  // not by their breathing room, so this costs nothing legible.
  padding: '4px 4px',
});

/** The verdict half drops the pill's 0.05em tracking. On a 250px chip row a
 *  twelve-character verdict pays ~7px for tracking it does not need — the half
 *  is already distinguished by its own fill, so the letter-spacing is buying
 *  nothing and costing a wrap. The STATUS half keeps it: that half is the one
 *  that has to look identical to every other chip on the board. */
const verdictHalfStyle = (t: { bg: string; text: string }): CSSProperties => ({
  ...chipHalfStyle(t),
  letterSpacing: 'normal',
});

/**
 * The clinical status chip. `variant="short"` (the board card) shows the
 * compact text; `variant="full"` (the drawer) keeps the whole phrase visible.
 * Both carry the FULL phrase as `title` + `aria-label` — hover and screen
 * reader always get the complete wording (WCAG 1.4.1: state is never colour
 * or icon alone).
 *
 * `verdict` (2026-09-06) splits the chip in two: the status keeps the whole
 * left half — same glyph, same amber, same width on every card — and the
 * verdict is a second fill butted against it. NO VERDICT RENDERS THE CHIP
 * EXACTLY AS IT ALWAYS WAS, down to the accessible name, which is load-bearing
 * in two directions: it is the fallback for a card whose flags we were never
 * sent (see `reviewVerdictFor`), and it is what the eight non-pre-decision
 * lanes and the drawer keep rendering untouched.
 *
 * ONE `role="img"` FOR THE WHOLE CHIP, never one per half. The halves are two
 * parts of a single claim; announcing them separately would give a screen
 * reader "review" and "clear" as unrelated chips, and would double the count
 * every test that sweeps the board for status chips is checking.
 */
export function ClinicalStatusChip({
  status,
  variant = 'short',
  verdict = null,
}: {
  status: ClinicalStatus;
  variant?: 'short' | 'full';
  verdict?: ReviewVerdict | null;
}) {
  const copy = STATUS_COPY[status];
  const Glyph = STATUS_GLYPH[status];
  const tone = STATUS_TOKENS[status];
  if (!verdict) {
    return (
      <span role="img" aria-label={copy.full} title={copy.full} style={chipStyle(tone)}>
        <Glyph />
        {variant === 'full' ? copy.full : copy.short}
      </span>
    );
  }
  const verdictCopy = VERDICT_COPY[verdict];
  const label = `${copy.full} — ${verdictCopy.full}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{
        // The pill's chrome WITHOUT the padding and the fill: the halves carry
        // both, and `overflow: hidden` is what crops them to the pill radius.
        ...chipStyle(tone),
        background: 'transparent',
        padding: 0,
        alignItems: 'stretch',
        overflow: 'hidden',
      }}
    >
      {/* ⚠️ ON THE CARD THE STATUS HALF IS THE GLYPH ALONE — no "Review". The
          first cut spelt the word out on both surfaces and the chip came out
          147.9px against the 74.7px badge it replaced, roughly double: on a
          250px row an `Action needed` card that fitted on one line on dev
          wrapped onto two and grew 30.5px. A change whose entire purpose is
          one card height cannot be the thing that makes a card taller.
          The word is redundant here anyway — the hourglass IS "pending", the
          amber IS "someone owes an action", and the lane is a review lane. The
          drawer (`variant="full"`) still spells everything out, and BOTH
          surfaces carry the complete phrase as `title` + `aria-label` on the
          wrapper above, so nothing is lost to hover or to a screen reader
          (WCAG 1.4.1 — never colour or icon alone). */}
      <span style={chipHalfStyle(tone)}>
        <Glyph />
        {variant === 'full' ? copy.full : null}
      </span>
      <span style={verdictHalfStyle(VERDICT_TOKENS[verdict])}>{variant === 'full' ? verdictCopy.full : verdictCopy.short}</span>
    </span>
  );
}

/* ── The questionnaire GHOSTS (2026-09-06) ──────────────────────────────────
 *
 * The two states where there is no clinical fact yet, drawn as GHOSTS — an
 * outline and muted ink, no fill — because they are the ABSENCE of a status
 * and must not look like one. Every filled pill on this card row is a claim
 * about the patient; these two say only how far the patient has got, and a
 * reader scanning a lane for work should be able to skip them without reading
 * the words.
 *
 * DASHED FOR "not started", SOLID FOR "in progress", and the difference is the
 * point rather than decoration: an unbroken outline is a thing that exists but
 * is not finished; a broken one is a thing that is not there. That reading
 * survives greyscale and a 50% zoom, which "two greys" would not.
 *
 * SENTENCE CASE, and this is the one place in the chip family that breaks the
 * uppercase rule. Two reasons, both load-bearing. It is a second, redundant
 * signal that these are not statuses. And "QUESTIONNAIRE NOT STARTED" set at
 * 11px/700 with 0.05em tracking is around 200px of a ~240px card — it would
 * take the whole row on its own and wrap the moment anything joined it, which
 * would grow the very card this change exists to hold still.
 *
 * VISIBLE TEXT, so no `role="img"` and no `aria-label`: the words ARE the
 * accessible name, which is the simplest way to satisfy WCAG 1.4.1 and keeps
 * these two out of the `role="img"` sweeps the board's chip tests run.
 * `--gps-muted` on `--gps-surface` is 5.8:1 (tokens.css) and the
 * `--gps-border-strong` outline is 4.08:1 — both past their bars.
 */
const GHOST_COPY = {
  not_started: 'Questionnaire not started',
  in_progress: 'Questionnaire in progress',
} as const;

export function QuestionnaireGhostChip({ state }: { state: 'not_started' | 'in_progress' }) {
  return (
    <span
      style={{
        ...chipStyle({ bg: 'transparent', text: 'var(--gps-muted)' }),
        border: `1px ${state === 'not_started' ? 'dashed' : 'solid'} var(--gps-border-strong)`,
        // ⚠️ 3px/9px, NOT the filled chip's 4px/10px, and the 1px it gives back
        // per edge is exactly the border. MEASURED IN CHROME, twice, because
        // the obvious answer is wrong: `boxSizing: 'border-box'` from chipStyle
        // does NOT make this the same height as a filled chip. Border-box means
        // the 22px FLOOR is measured across the border — but the ghost's own
        // border-box height is 13.2 line + 8 padding + 2 border = 23.2px, which
        // is ABOVE the floor, so the floor never binds and the ghost stands
        // 1.2px proud of every chip beside it. A card's height would then
        // depend on whether a patient had opened the form, which is the ragged
        // stack this whole slot exists to remove.
        // Paying the border out of the padding puts the content box back to
        // 21.2px, the floor binds, and both chips measure exactly 22px.
        // Verified: filled 22.0, ghost 22.0. Do not "tidy" this back to 4px/10px.
        padding: '3px 9px',
        fontWeight: 600,
        letterSpacing: 'normal',
        textTransform: 'none',
      }}
    >
      {GHOST_COPY[state]}
    </span>
  );
}

/* ── The review PARK (2026-08-27, CTO) ──────────────────────────────────────
 *
 * Orthogonal to ClinicalStatus, for the reason set out in the module doc: a
 * park is a HOLD on a review, not an answer to it. Kept in this module rather
 * than in the board so the drawer derives the same fact from the same line —
 * the two surfaces disagreeing about a card's state is the failure mode this
 * whole module exists to prevent.
 */

/**
 * Is the deal parked with an admin? `pending_admin_review` is the
 * clinician→admin park, entered by clinic-service's dedicated
 * pending-admin-review endpoint. The STAGE is the whole fact — there is no
 * separate flag on the wire, and R41 means the portal must not invent one.
 */
export function isReviewParked(card: DealCard): boolean {
  return card.stage === 'pending_admin_review';
}

/**
 * The park, as a compact ICON-ONLY chip — the CTO's ask verbatim: "i would
 * enter just the pause icon but as the last tag".
 *
 * WHY ICON-ONLY HERE, when the clinical-status chip is required to keep its
 * text. The two chips answer different questions and pay different prices for
 * the space. The status chip is the card's PRIMARY claim and its three states
 * are told apart by hue as much as by glyph, so dropping its text would put
 * real weight on colour; the park is a SECONDARY qualifier on a status that is
 * now visible right beside it, its meaning is carried by a universally read
 * shape (the transport pause bars), and it is the only chip on the card drawn
 * as two plain vertical strokes — so it is unambiguous at 12px, in greyscale,
 * and to a colour-blind reader. That is the same trade the entry-path chips
 * already make in this module, and it is what buys back the row width the text
 * pill was spending.
 *
 * WCAG 2.1 AA. 1.4.1 is satisfied by SHAPE, not colour. 1.1.1 / 4.1.2: the
 * full wording ships as `aria-label` + `title` on a `role="img"` element, so a
 * screen reader announces "Review parked" exactly as it did when the phrase
 * was visible text, and a hover reveals it for a sighted reader who does not
 * know the glyph. Contrast: `--gps-muted` on `--gps-surface-2` is ~5.3:1,
 * comfortably past the 3:1 bar 1.4.11 sets for a meaningful graphic (and past
 * the 4.5:1 text bar the old pill had to clear, so nothing got fainter).
 *
 * NOT INTERACTIVE, deliberately: it is a status marker on a card that is
 * itself the button, so it takes no tab stop of its own.
 */
export function ReviewParkedChip() {
  return (
    <span role="img" aria-label={PARKED_LABEL} title={PARKED_LABEL} style={chipStyle(PARKED_TOKENS)}>
      <PauseGlyph />
    </span>
  );
}

/* ── PAID BUT NOT WITH THE PHARMACY — NOT A CHIP YET (2026-09-11) ───────────
 *
 * ⚠️ THIS WAS BUILT AND THEN DELIBERATELY NOT SHIPPED, so the next person does
 * not rebuild it and hit the same wall.
 *
 * The 2026-09-09 incident — a patient charged £192.50 whose dispensing request
 * was refused 400ms later, with every surface reading as settled — argues for
 * saying it on the BOARD, because a drawer has to be opened and nobody opens a
 * card they have no reason to suspect. A chip was written to do that, keyed on
 * `paymentPosture.state === 'captured' && !orderReachedPharmacy(orderState)`.
 *
 * IT CANNOT BE TRUE ON THIS SURFACE YET. Since orders-service #236 deleted the
 * auto-issue on capture, the ordinary resting place of a freshly approved order
 * is paid, at `approved`, with no prescription written — the clinician's normal
 * queue — and that satisfies the condition above exactly. A board card carries
 * only `DealPaymentPosture`, which has no prescription fact (`orderState` plus
 * money), so the chip cannot tell that case from a real stall. It would ride
 * most Won cards, and a warning on most cards is the noise that made the
 * original defect invisible — the same failure mode in a different colour.
 *
 * WHAT IT NEEDS: `awaitingPrescription` (or an equivalent) on the deal card's
 * posture. orders-service already answers it on the staff order projection
 * (`staffOrderWithReapproval`, dto.ts), so this is a clinic-service posture
 * addition, not new truth. Until then the DRAWER states it — journeyModel case
 * 0b, which has the order in hand and whose wording follows the evidence — and
 * the two blocked-send audit actions below make the durable record findable.
 */

/* ── Entry-path icon chips (§4; strengthened 2026-08-27, re-drawn 2026-09-06) ─
 * All FOUR provenance lanes are now compact icon chips — a document for the two
 * form lanes, a handset for the callback lane, a person for the staff-created
 * lane — with the full wording on `title` + `aria-label` so nothing is lost to
 * assistive tech or hover.
 *
 * ── 2026-09-06 (CTO): THE FORM CHIP WAS INVISIBLE, AND IT WAS A DRAWING BUG ──
 *
 * The 2026-08-27 pass separated the callback chip from the form chip by giving
 * the handset a SOLID silhouette on a SOLID fill. It never touched the form
 * chip, so the pair ended up with BOTH variables pointing the same way: a
 * filled shape on a strong fill beside a 2-weight hairline outline on a pale
 * tint. Next to each other at 12px the form chip did not read as a different
 * provenance, it read as an empty pill. Two fixes, because there were two
 * faults:
 *
 *  1. MASS. `FormGlyph` is now a solid silhouette at the handset's weight, so
 *     the two glyphs are separated by SHAPE (a page vs a handset) rather than
 *     by how much ink each spends. That is the distinction that survives being
 *     shrunk, blurred, and rendered in greyscale.
 *  2. HUE. The form chip was the pale BRAND GREEN, i.e. a second green in a row
 *     whose colour language is status — green approved, amber waiting, red
 *     stopped. PROVENANCE IS NOT A STATUS, so it must not borrow one, and a
 *     second green also put the two sources a shade apart. It takes the indigo
 *     from `ui/tones` CHIP_TONES — a hue the status vocabulary does not use, so
 *     it cannot be misread as a verdict. See that module for the WCAG figures
 *     and for why the value is a literal rather than a token.
 *
 * `staff_sql` KEPT A TEXT BADGE UNTIL NOW and it was the odd one out for a
 * worse reason than looks: an uppercase word in a pale-green pill is the exact
 * shape every STATUS badge on this board wears, so the one chip whose job was
 * to say "a member of staff typed this in" was the one most likely to be read
 * as a clinical verdict. It is a person glyph on the muted tone now — grey,
 * because staff provenance is the neutral one of the four, and an icon,
 * because all four provenance chips being one family is what lets the eye
 * treat that slot on the card as a single question.
 */

/**
 * The FORM page — a SOLID silhouette with its rule lines punched out
 * (`fillRule="evenodd"`, so the three inner rectangles are holes rather than
 * ink). Drawn as mass rather than as an outline for the reason in the block
 * above: at 12px an outline is one device pixel and simply disappears beside
 * the filled handset it has to be told apart from.
 */
function FormGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Zm2 5h8v2.6H8V7Zm0 4.4h8V14H8v-2.6Zm0 4.4h5v2.6H8v-2.6Z"
      />
    </svg>
  );
}

/**
 * The callback handset — deliberately a SOLID silhouette, not the outline the
 * clinical-status glyphs use (2026-08-27, stage clarity §3).
 *
 * WHY: the wave-1 pair was already two DIFFERENT drawings (a lined document vs
 * a handset), but at the 12px card size both were thin outlines inside an
 * IDENTICAL pale-green pill, and the only thing separating them was a line
 * pattern a few pixels across. That was survivable while a card's lane implied
 * its provenance; it is not now that cards CHANGE LANE on approval and the icon
 * is the only thing left carrying where the person came from. Filling the
 * handset separates the two by MASS rather than by line detail, so they stay
 * apart when shrunk, when blurred, and in greyscale — the shape test that a
 * hue swap alone cannot pass.
 *
 * UNCHANGED by the 2026-09-06 pass, deliberately: it was the half of the pair
 * that was already right, and the form chip was brought up to meet it.
 */
function PhoneGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path
        fill="currentColor"
        d="M6.6 2.5a1.6 1.6 0 0 1 1.9.8l1.7 3.4a1.6 1.6 0 0 1-.4 1.9L8.3 10a13 13 0 0 0 5.7 5.7l1.4-1.5a1.6 1.6 0 0 1 1.9-.4l3.4 1.7a1.6 1.6 0 0 1 .8 1.9l-.6 2.3a1.6 1.6 0 0 1-1.7 1.2A17.5 17.5 0 0 1 3 6.4a1.6 1.6 0 0 1 1.2-1.7z"
      />
    </svg>
  );
}

/**
 * The STAFF-CREATED lane's person — head and shoulders, solid, at the same
 * weight as the other two provenance glyphs (2026-09-06). A person is the one
 * shape that says "somebody here typed this in" without a word, and it is
 * unmistakable against a page and a handset at 12px.
 */
function PersonGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg {...svgProps(size)}>
      <path
        fill="currentColor"
        d="M12 12.2a4.6 4.6 0 1 0 0-9.2 4.6 4.6 0 0 0 0 9.2Zm0 1.8c-4.3 0-7.8 2.5-7.8 5.6v.5c0 .5.4.9.9.9h13.8c.5 0 .9-.4.9-.9v-.5c0-3.1-3.5-5.6-7.8-5.6Z"
      />
    </svg>
  );
}

/** Which glyph an entry path wears. ALL FOUR are icon chips since 2026-09-06. */
const ENTRY_GLYPH: Partial<Record<DealEntryPath, (props: { size?: number }) => ReactElement>> = {
  widget_form: FormGlyph,
  portal_journey: FormGlyph,
  callback_button: PhoneGlyph,
  staff_sql: PersonGlyph,
};

/** The pale brand tint — now only the SAFE DEFAULT for a path the server adds
 *  before this module knows about it (see EntryPathChip). No lane wears it. */
const ENTRY_TINT = { bg: 'var(--gps-primary-light)', text: 'var(--gps-green-text)' } as const;

/**
 * The CALLBACK chip's inverted tone (2026-08-27, stage clarity §3) — the brand
 * hue at full strength with white on it, so provenance still reads as
 * provenance rather than as a status.
 *
 * WHY INVERT rather than pick a new hue FOR THIS ONE: a solid fill is unused by
 * every status chip on the board, so it cannot be mistaken for a verdict, and
 * it keeps the callback lane in the green family staff already associate with
 * it. (The FORM lanes did take a new hue in the end — see CHIP_TONES.indigo and
 * the block at the top of this section for why one inversion was not enough to
 * separate two chips that also had to be separated from each other.)
 *
 * WCAG 2.1 AA: `--gps-on-accent` (white) on `--gps-primary` is the measured
 * 5.13:1 pair the button fills are built on (see tokens.css) — comfortably past
 * both the 4.5:1 text bar and the 3:1 non-text/graphics bar (1.4.11) the glyph
 * itself needs. Nothing here relies on colour alone: the chip's full wording
 * ships as `title` + `aria-label`, exactly as before.
 */
const ENTRY_SOLID = { bg: 'var(--gps-primary)', text: 'var(--gps-on-accent)' } as const;

/**
 * Per-path chip tone. THREE HUES FOR THREE KINDS OF ARRIVAL, and no two of them
 * are a shade of each other:
 *
 *  - the two FORM lanes share the INDIGO — they are the same kind of
 *    provenance (a form the person filled in themselves) and their aria-labels
 *    already tell "Widget form" from "Started online";
 *  - CALLBACK takes the solid brand green, because "they asked us to ring
 *    them" is the one provenance that changes how staff open the conversation;
 *  - `staff_sql` takes the MUTED grey: a deal a colleague keyed in is the
 *    neutral case, and grey is the tone this board already spends on "true, but
 *    not something to act on".
 *
 * Read from `ui/tones` CHIP_TONES rather than written out here — a chip colour
 * invented at a call site is how the form chip ended up a second green.
 */
const ENTRY_TOKENS: Partial<Record<DealEntryPath, { bg: string; text: string }>> = {
  widget_form: CHIP_TONES.indigo,
  portal_journey: CHIP_TONES.indigo,
  callback_button: ENTRY_SOLID,
  staff_sql: CHIP_TONES.muted,
};

/**
 * The entry-path chip: one compact icon chip per provenance lane, carrying its
 * full wording on `title` + `aria-label`.
 *
 * WCAG 2.1 AA. 1.4.1 is satisfied by SHAPE — page, handset, person — not by the
 * three hues, which is why the glyphs had to stop being interchangeable
 * hairlines before the hues were worth choosing. 1.1.1 / 4.1.2: `role="img"`
 * plus the label means a screen reader announces "Widget form" exactly as the
 * old text badge did.
 *
 * THE TEXT FALLBACK IS NOT DEAD CODE. Every path in `DealEntryPath` today has a
 * glyph, but clinic-service can add one before this module knows about it, and
 * a card that renders NOTHING where its provenance should be is worse than one
 * that renders the raw label — so an unknown path still gets a chip.
 */
export function EntryPathChip({ entryPath }: { entryPath: DealEntryPath }) {
  const Glyph = ENTRY_GLYPH[entryPath];
  if (!Glyph) {
    // A future path with no glyph yet: the text badge stands, on the tint.
    return (
      <span title={ENTRY_LABEL[entryPath]} style={chipStyle(ENTRY_TINT)}>
        {ENTRY_LABEL[entryPath]}
      </span>
    );
  }
  return (
    <span
      role="img"
      aria-label={ENTRY_LABEL[entryPath]}
      title={ENTRY_LABEL[entryPath]}
      // A path with a glyph but no tone entry (added server-first) still
      // renders — on the pale tint, the safe default.
      style={chipStyle(ENTRY_TOKENS[entryPath] ?? ENTRY_TINT)}
    >
      <Glyph />
    </span>
  );
}
